import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  AdminCreateTenantRequestSchema,
  PLANS,
  BILLING_CYCLES,
  formatPhp,
  type AdminCreateTenantRequest,
  type BroadcastSegmentKey,
  type PlanCode,
  type ProgramCaps,
} from '@naya/shared'
import { AdminLayout } from '../components/AdminLayout.js'
import { BroadcastCadenceFields } from '../components/BroadcastCadenceFields.js'
import { BroadcastCapabilityFields } from '../components/BroadcastCapabilityFields.js'
import { ProgramCapsFields } from '../components/ProgramCapsFields.js'
import { useCreateTenant } from '../hooks/useCreateTenant.js'
import { usePlanTiers } from '../hooks/useTenants.js'
import { AdminApiError } from '../adminApi.js'

const PLAN_LABEL: Record<PlanCode, string> = {
  starter: 'Starter',
  pro: 'Pro',
}

/**
 * Slice 6 — minimal tenant create. The owner provides their own email,
 * name, business name, and URL handle during the setup wizard, so this form
 * collects only plan/billing/seat caps/notes. Plan tier and billing cycle
 * remain admin-controlled but are informational under manual payment.
 */
export function TenantCreate() {
  const navigate = useNavigate()
  const create = useCreateTenant()
  const { data: planData } = usePlanTiers()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AdminCreateTenantRequest>({
    resolver: zodResolver(AdminCreateTenantRequestSchema),
    defaultValues: {
      plan_tier: 'starter',
      billing_cycle: 'monthly',
      max_staff: 1,
      max_branches: 1,
    },
  })

  const billingCycle = watch('billing_cycle')

  // Broadcast cadence local state. Initialised null, then pre-filled from
  // the selected plan tier by applyTierDefaults below (the admin can still
  // override). null = unlimited (no hard cap) on that window. Encoded
  // outside react-hook-form so the "unlimited" checkbox can clear the
  // number input cleanly.
  const [perDay, setPerDay] = useState<number | null>(null)
  const [perWeek, setPerWeek] = useState<number | null>(null)
  // Per-tenant broadcast-capability override. null = inherit the plan tier
  // (the default for a new tenant); an explicit value = override.
  const [presetsEnabled, setPresetsEnabled] = useState<
    BroadcastSegmentKey[] | null
  >(null)
  const [filterBuilderEnabled, setFilterBuilderEnabled] = useState<
    boolean | null
  >(null)
  // Per-tenant program-caps override. null = inherit the plan tier default
  // (the default for a new tenant); a map = explicit override.
  const [programCaps, setProgramCaps] = useState<ProgramCaps | null>(null)

  function priceFor(tier: PlanCode): string {
    const row = planData?.plan_tiers.find((p) => p.tier === tier)
    if (!row) return ''
    const centavos =
      billingCycle === 'annual'
        ? row.annual_price_centavos
        : row.monthly_price_centavos
    return `${formatPhp(centavos)}/${billingCycle === 'annual' ? 'yr' : 'mo'}`
  }

  // Pre-fill the seat / branch / broadcast-cap fields from the selected
  // plan tier. The admin can still override any field before submitting —
  // this just replaces the old hardcoded "1 / 1 / unlimited" defaults with
  // sensible per-tier ones. Re-applies whenever the admin switches tier.
  const planTierReg = register('plan_tier')
  function applyTierDefaults(tier: PlanCode) {
    const row = planData?.plan_tiers.find((p) => p.tier === tier)
    if (!row) return
    // A null tier staff cap means "unlimited" (Pro) — tenants.max_staff is a
    // NOT-NULL hard cap, so pre-fill the schema max (100, effectively
    // unlimited for this market). The admin can still override.
    setValue('max_staff', row.max_staff_per_branch ?? 100)
    setValue('max_branches', row.max_branches ?? 1)
    setPerDay(row.max_broadcasts_per_day)
    setPerWeek(row.max_broadcasts_per_week)
  }

  // Apply the initial tier's defaults once plan-tier data has loaded (the
  // form mounts before the query resolves).
  const didInitDefaults = useRef(false)
  useEffect(() => {
    if (didInitDefaults.current || !planData) return
    didInitDefaults.current = true
    applyTierDefaults(watch('plan_tier'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planData])

  function onSubmit(values: AdminCreateTenantRequest) {
    const payload: AdminCreateTenantRequest = {
      ...values,
      ...(values.notes && values.notes.trim()
        ? { notes: values.notes.trim() }
        : {}),
      max_broadcasts_per_day: perDay,
      max_broadcasts_per_week: perWeek,
      broadcast_presets_enabled: presetsEnabled,
      broadcast_filter_builder_enabled: filterBuilderEnabled,
      program_caps: programCaps,
    }
    create.mutate(payload, {
      onSuccess: (res) => {
        navigate(`/admin/tenants/${res.tenant.id}?just_created=1`, {
          state: {
            setup: {
              setup_link: res.setup_link,
              pre_filled_message_en: res.pre_filled_message_en,
              pre_filled_message_tl: res.pre_filled_message_tl,
            },
          },
        })
      },
      onError: (err) => {
        if (err instanceof AdminApiError) {
          toast.error(err.userMessage)
          return
        }
        toast.error('Something went wrong — try again.')
      },
    })
  }

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <h1>Create tenant</h1>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="admin-card"
        style={{ maxWidth: 560 }}
      >
        <p className="admin-hint" style={{ marginTop: 0, marginBottom: 16 }}>
          The owner sets their business name, URL handle, email, and name
          during their setup wizard. You just choose the plan + seat caps and
          send them the link.
        </p>

        <div className="admin-field">
          <label className="admin-label">Plan tier</label>
          <div className="admin-radio-group">
            {PLANS.map((p) => (
              <label key={p} className="admin-radio-opt">
                <input
                  type="radio"
                  value={p}
                  {...planTierReg}
                  onChange={(e) => {
                    void planTierReg.onChange(e)
                    applyTierDefaults(e.target.value as PlanCode)
                  }}
                />
                <span>{PLAN_LABEL[p]}</span>
                <span className="admin-radio-price">{priceFor(p)}</span>
              </label>
            ))}
          </div>
          {errors.plan_tier && (
            <p className="admin-field-error">{errors.plan_tier.message}</p>
          )}
        </div>

        <div className="admin-field">
          <label className="admin-label">Billing cycle</label>
          <div className="admin-radio-group">
            {BILLING_CYCLES.map((c) => (
              <label key={c} className="admin-radio-opt">
                <input type="radio" value={c} {...register('billing_cycle')} />
                <span style={{ textTransform: 'capitalize' }}>{c}</span>
                {c === 'annual' && (
                  <span className="admin-radio-price">1 month free</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div className="admin-field">
          <label className="admin-label" htmlFor="max_staff">
            Staff seats
          </label>
          <input
            id="max_staff"
            type="number"
            min={0}
            max={100}
            className="admin-input"
            {...register('max_staff', { valueAsNumber: true })}
          />
          <p className="admin-hint">
            Number of staff the owner can add. The owner themselves doesn't
            count. Pre-filled from the plan tier — override if needed.
          </p>
          {errors.max_staff && (
            <p className="admin-field-error">{errors.max_staff.message}</p>
          )}
        </div>

        <div className="admin-field">
          <label className="admin-label" htmlFor="max_branches">
            Branches
          </label>
          <input
            id="max_branches"
            type="number"
            min={1}
            max={100}
            className="admin-input"
            {...register('max_branches', { valueAsNumber: true })}
          />
          <p className="admin-hint">
            Maximum number of branches the owner can create. Pre-filled from
            the plan tier — override if needed.
          </p>
          {errors.max_branches && (
            <p className="admin-field-error">{errors.max_branches.message}</p>
          )}
        </div>

        <BroadcastCadenceFields
          perDay={perDay}
          perWeek={perWeek}
          onPerDayChange={setPerDay}
          onPerWeekChange={setPerWeek}
        />

        <BroadcastCapabilityFields
          presetsEnabled={presetsEnabled}
          filterBuilderEnabled={filterBuilderEnabled}
          onChange={(presets, builder) => {
            setPresetsEnabled(presets)
            setFilterBuilderEnabled(builder)
          }}
        />

        <div className="admin-field">
          <label className="admin-label">Pass types &amp; limits</label>
          <label className="admin-check-row">
            <input
              type="checkbox"
              checked={programCaps !== null}
              onChange={(e) =>
                e.target.checked
                  ? setProgramCaps({
                      loyalty: 1,
                      membership: 0,
                      event_ticket: 0,
                    })
                  : setProgramCaps(null)
              }
            />
            Override the plan tier for this tenant
          </label>
          {programCaps === null ? (
            <p className="admin-hint" style={{ marginTop: 4 }}>
              This tenant inherits its pass types &amp; limits from its plan
              tier. Tick to set per-type caps now.
            </p>
          ) : (
            <div style={{ marginLeft: 22, marginTop: 6 }}>
              <p className="admin-hint" style={{ marginTop: 0 }}>
                Max programs the owner can create, per type:
              </p>
              <ProgramCapsFields
                value={programCaps}
                onChange={setProgramCaps}
                idPrefix="create-caps"
              />
            </div>
          )}
        </div>

        <div className="admin-field">
          <label className="admin-label">Referrals</label>
          <label className="admin-check-row">
            <input type="checkbox" {...register('referral_entitled')} />
            Referral entitled
          </label>
          <p className="admin-hint">
            Lets the owner run a referral program. Off by default — leave
            unchecked unless this tenant has a real customer base to spread it.
          </p>
        </div>

        <div className="admin-field">
          <label className="admin-label">Secret menu</label>
          <label className="admin-check-row">
            <input type="checkbox" {...register('secret_menu_entitled')} />
            Secret menu entitled
          </label>
          <p className="admin-hint">
            Lets the owner tag stamps issued off the standard menu ("secret
            menu"). Off by default.
          </p>
        </div>

        <div className="admin-field">
          <label className="admin-label" htmlFor="notes">
            Notes (optional)
          </label>
          <textarea
            id="notes"
            className="admin-textarea"
            autoComplete="off"
            {...register('notes')}
          />
          {errors.notes && (
            <p className="admin-field-error">{errors.notes.message}</p>
          )}
        </div>

        <div className="admin-form-actions">
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={isSubmitting || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create tenant'}
          </button>
          <button
            type="button"
            className="admin-btn"
            onClick={() => navigate('/admin/tenants')}
          >
            Cancel
          </button>
        </div>
      </form>
    </AdminLayout>
  )
}
